import {
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  QueryList
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GraphComponent } from '../../graph/graph';
import { Container } from '../container';

@Component({
  selector: 'screen-group-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.less']
})
export class CarouselComponent extends Container implements AfterViewInit, OnDestroy {
  private _isHorizontal = true;
  private _backfaceInvisible = true;
  private _autoplay = true;
  private _interval = 6000;
  private _intervalHandle;

  private _initialized = false;
  private _carousel: HTMLElement;
  private _length: number;
  private _totalPanelCount: number;

  private _currentIndex = 0;
  private _changesSubscription: Subscription;

  @ContentChildren(GraphComponent) graphs: QueryList<GraphComponent>;


  @Input()
  set horizontal(param: boolean) {
    this._isHorizontal = !!param;
    if (this._initialized) {
      this._changeCarousel();
    }
  }

  @Input()
  set autoplay(param: boolean) {
    this._autoplay = !!param;
    if (this._initialized) {
      this._hackAutoplay();
    }
  }

  @Input()
  set interval(param: number) {
    if (Number.isInteger(param) && param > 1000 && this._interval !== param) {
      this._interval = param;
      if (this._initialized) {
        this.start();
      }
    }
  }

  @HostBinding('class.hidden') _hidden = true;

  @Input()
  set hidden(param: boolean) {
    this._hidden = !!param;
  }

  @Input()
  set backfaceInvisible(param: boolean) {
    this._backfaceInvisible = !!param;
  }

  get backfaceInvisible() {
    return this._backfaceInvisible;
  }

  constructor(private _elementRef: ElementRef) {
    super();
  }

  start() {
    this.stop();
    this._intervalHandle = setInterval(() => {
      this.next();
    }, this._interval);
  }

  /**
   * 停止轮播
   */
  stop() {
    if (this._intervalHandle) {
      clearInterval(this._intervalHandle);
      this._intervalHandle = 0;
    }
  }

  resize() {
    this._changeCarousel();
  }

  previous() {
    this._currentIndex--;
    this._rotateCarousel();
  }

  next() {
    this._currentIndex++;
    this._rotateCarousel();
  }

  stepBy(increment: number) {
    this._currentIndex += increment;
    this._rotateCarousel();
  }

  stepTo(index: number) {
    this._currentIndex = (index < 0 || index >= this._length) ? 0 : index;
    this._rotateCarousel();
  }

  ngAfterViewInit() {
    this._carousel = this._elementRef.nativeElement.querySelector('.carousel');
    this._init();

    this.addSubscription(() => {
      this.stop();
    });

    this.addSubscription(this.graphs.changes.subscribe(() => {
      this._init();
    }));
  }

  private _init() {
    // 确定活动graph的数量
    this._length = this.graphs.length;
    this._totalPanelCount = this._carousel.children.length;

    this._initialized = true;

    this._changeCarousel();
    this._hackAutoplay();
  }

  private _hackAutoplay() {
    if (this._autoplay) {
      this.start();
    }
  }

  private get _size(): number {
    return this._carousel[this._isHorizontal ? 'offsetWidth' : 'offsetHeight'];
  }

  private get _rotateFn(): string {
    return this._isHorizontal ? 'rotateY' : 'rotateX';
  }

  private get _theta(): number {
    return this._length ? 360 / this._length : 0;
  }

  /**
   * do some trig to figure out how big the carousel is in 3D space
   * @returns {number}
   */
  private get _radius(): number {
    return Math.round((this._size / 2) / Math.tan(Math.PI / this._length));
  }

  /**
   * 根据当前索引，旋转轮播组件
   * @private
   */
  private _rotateCarousel() {
    const angle = this._theta * this._currentIndex * -1;
    this._carousel.style.transform = `translateZ(${-this._radius}px) ${this._rotateFn}(${angle}deg)`;
  }

  /**
   * 初始化或重置轮播的设置
   * 该方法调用的前提是已经初始化
   * @private
   */
  private _changeCarousel() {
    let panel, angle, i;

    for (i = 0; i < this._length; i++) {
      panel = this._carousel.children[i];
      angle = this._theta * i;
      panel.style.opacity = 1;
      panel.style.display = 'block';
      // rotate panel, then push it out in 3D space
      panel.style.transform = this._rotateFn + '(' + angle + 'deg) translateZ(' + this._radius + 'px)';
    }

    // hide other panels
    for (; i < this._totalPanelCount; i++) {
      panel = this._carousel.children[i];
      panel.style.opacity = 0;
      panel.style.display = 'none';
      panel.style.transform = 'none';
    }

    this._rotateCarousel();
  }
}
